import { CSSProperties, ReactNode } from 'react';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../context';
import { tokens } from '../tokens';
import { pick } from '../responsive';
import EditorialHeader from './shared/EditorialHeader';

interface ChapterProps {
  num: string;
  title: string;
  intro: string;
  accent: string;
  body: ReactNode;
  layout: CSSProperties;
  noTopBorder?: boolean;
}

const Chapter = ({
  num,
  title,
  intro,
  accent,
  body,
  layout,
  noTopBorder,
}: ChapterProps) => (
  <div
    style={{
      padding: '32px 0',
      borderTop: noTopBorder ? 'none' : tokens.borderFaint,
    }}
  >
    <div style={layout}>
      <div>
        <div
          style={{
            fontSize: 13,
            color: accent,
            fontFamily: tokens.fontMono,
            letterSpacing: '0.15em',
            marginBottom: 6,
          }}
        >
          {num}
        </div>
        <div
          style={{
            fontSize: 40,
            fontFamily: tokens.fontDisplay,
            lineHeight: 0.95,
            letterSpacing: '-0.01em',
            color: tokens.textPrimary,
            whiteSpace: 'pre-line',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 16,
            color: tokens.textTertiary,
            marginTop: 16,
            lineHeight: 1.6,
            maxWidth: 380,
          }}
        >
          {intro}
        </div>
      </div>
      <div>{body}</div>
    </div>
  </div>
);

const cell = (
  label: string,
  value: string,
  accent: string,
  fontSize: number,
) => (
  <div
    key={label}
    style={{
      background: tokens.backgroundBase,
      padding: '18px 22px',
      borderTop: `2px solid ${accent}55`,
    }}
  >
    <div
      style={{
        fontSize: 13,
        color: accent,
        fontFamily: tokens.fontMono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize,
        fontFamily: tokens.fontDisplay,
        color: tokens.textPrimary,
        marginTop: 4,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
  </div>
);

const Config = () => {
  const displayData = useResultData();
  const { breakpoint } = useWidthBreakpoint();

  const padding = pick(breakpoint, {
    sm: '4px 0',
    md: '4px 0',
    lg: '4px 0',
  });

  // Chapter layout: lg/md = 4fr/8fr grid; sm = stacked
  const chapterLayout: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column', gap: 24 },
    md: {
      display: 'grid',
      gridTemplateColumns: '4fr 8fr',
      gap: 32,
      alignItems: 'flex-start',
    },
    lg: {
      display: 'grid',
      gridTemplateColumns: '4fr 8fr',
      gap: 64,
      alignItems: 'flex-start',
    },
  });

  // Inner value grids
  const timeframeColumns = pick(breakpoint, { sm: 1, md: 3, lg: 3 });
  const complexParamColumns = pick(breakpoint, { sm: 2, md: 4, lg: 4 });
  const mrcColumns = pick(breakpoint, { sm: 2, md: 3, lg: 3 });

  const timeframeSize = pick(breakpoint, { sm: 28, md: 32, lg: 36 });
  const valueSize = pick(breakpoint, { sm: 22, md: 26, lg: 28 });
  const indicatorSize = pick(breakpoint, { sm: 16, md: 18, lg: 20 });

  const grid = (
    columns: number,
    entries: Array<[string, string]>,
    accent: string,
    fontSize: number,
  ) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 4,
      }}
    >
      {entries.map(([label, value]) => cell(label, value, accent, fontSize))}
    </div>
  );

  // Chapter 3 indicators: side-by-side on lg, stacked on md/sm
  const indicatorSplit: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column', gap: 24 },
    md: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    lg: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  });

  return (
    <div
      style={{
        background: tokens.backgroundBase,
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <EditorialHeader number="03" label="How It Was Built" />

      <Chapter
        num="Chapter 1"
        title={'Timeframes &\nComplex Params.'}
        intro="Three timeframes feed the algorithm simultaneously. Complex params control the cube-root price band and mean-reversion toggle."
        accent={tokens.green}
        layout={chapterLayout}
        noTopBorder
        body={
          <>
            {grid(
              timeframeColumns,
              Object.entries(displayData.timeframes) as Array<[string, string]>,
              tokens.green,
              timeframeSize,
            )}
            <div style={{ height: 4 }} />
            {grid(
              complexParamColumns,
              Object.entries(displayData.complexParams) as Array<
                [string, string]
              >,
              tokens.green,
              valueSize,
            )}
          </>
        }
      />

      <Chapter
        num="Chapter 2"
        title="Mrc state config."
        intro="Mean-reversion classifier feeds back into the strategy when complex.mrcState is enabled."
        accent={tokens.amber}
        layout={chapterLayout}
        body={grid(
          mrcColumns,
          Object.entries(displayData.mrcStateConfig) as Array<[string, string]>,
          tokens.amber,
          valueSize,
        )}
      />

      <Chapter
        num="Chapter 3"
        title="Take profit indicator config."
        intro="Two RSI-based indicators run side by side — opposite-order watches reversals, same-order watches trend continuation."
        accent={tokens.cyan}
        layout={chapterLayout}
        body={
          <div style={indicatorSplit}>
            <div>
              <div
                style={{
                  fontSize: 13,
                  color: tokens.cyan,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  marginBottom: 8,
                  fontWeight: 700,
                }}
              >
                OPPOSITE ORDER
              </div>
              {grid(
                1,
                Object.entries(displayData.indicatorConfig.opposite) as Array<
                  [string, string]
                >,
                tokens.cyan,
                indicatorSize,
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  color: tokens.cyan,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  marginBottom: 8,
                  fontWeight: 700,
                }}
              >
                SAME ORDER
              </div>
              {grid(
                1,
                Object.entries(displayData.indicatorConfig.same) as Array<
                  [string, string]
                >,
                tokens.cyan,
                indicatorSize,
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Config;
